import axios, {
  AxiosError,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import { get, set } from 'lodash';
import * as Sentry from '@sentry/nextjs';
import { useTokens } from '../store/auth/useTokens';
import { getBrokerFromHostName } from './broker';

const USER_AUTH_BASE_URL =
  process.env.NEXT_PUBLIC_USER_AUTH_BASE_URL ||
  'https://prometheus-user-app-dev.bridgeweave.net:4456';

const timeout = 3 * 10 * 1000; // 10 seconds

function attachAuthorization(config: InternalAxiosRequestConfig) {
  const broker = getBrokerFromHostName();
  const endpointsToAttachTenantId = Object.values(AuthEndpoints).concat(
    Object.values(ProfileEndpoints),
    AuthEndpoints.ssoClient(broker),
    AuthEndpoints.disclaimer(broker)
  );
  if (endpointsToAttachTenantId.includes(config.url as string)) {
    set(config, 'headers.X-Tenant-Id', broker);
  }
  if (config.url === AuthEndpoints.login) {
    return config; // Don't attach request token for login request
  }
  const { accessToken } = useTokens.getState();

  if (accessToken && !get(config, ['headers', 'Authorization'])) {
    set(config, 'headers.Authorization', `Bearer ${accessToken}`);
  }

  return config;
}

function requestErrorHandler(error: AxiosError) {
  const status = error?.response?.status;
  const url = error?.config?.url;
  const method = error?.config?.method?.toUpperCase();
  const baseURL = error?.config?.baseURL;
  
  try {
    Sentry.captureException(error, {
      tags: {
        error_type: 'api_error',
        http_status: status?.toString() ,
        http_method: method ,
        api_url: url,
        api_base_url: baseURL,
      },
      extra: {
        url: error?.config?.url,
        method: error?.config?.method,
        baseURL: error?.config?.baseURL,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        responseData: error?.response?.data,
        requestData: error?.config?.data,
        headers: error?.response?.headers,
        message: error?.message,
        code: error?.code,
      },
      level: 'error',
    });
  } catch (sentryError) {
    console.error('Failed to log to Sentry:', sentryError);
    console.error('Original error:', error);
  }

  if (status === HttpStatusCode.Unauthorized) {
    useTokens.getState().clearTokens();
    
    Sentry.captureMessage('Session Timeout', {
      level: 'warning',
      tags: {
        error_type: 'session_timeout',
        http_status: '401',
        action: 'redirect_to_error_page',
      },
      extra: {
        url: error?.config?.url,
        method: error?.config?.method,
        baseURL: error?.config?.baseURL,
      },
    });
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = error.response?.data as any;
  error.message = data?.message || error.message;
  return Promise.reject(error);
}

export const authFetcher = axios.create({
  baseURL: USER_AUTH_BASE_URL,
  timeout,
});

export const masterDataFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_MASTER_DATA_BASE_URL,
  timeout,
});

export const masterDataEndpoints = {
  getBrokerConfig: (broker: string, product?: string, category?: string) =>
    `broker/config/keys?broker=${broker.toLowerCase()}&product=${product}&category=${category}`,
  getCompanyDetails: (tickerList: string[]) =>
    `company/details?symbols=${tickerList.toString()}`,
  getLearnConfig: (broker: string) =>
    `/constants/learn?broker=${broker.toLowerCase()}`,
  branding: 'branding/',
};

export const intradayPrivateFetcher = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_INTRADAY_BASE_URL ||
    'https://intraday-youtrade.bridgeweave.net:453',
  timeout,
});

export const intradayPublicFetcher = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_INTRADAY_BASE_URL ||
    'https://intraday-youtrade.bridgeweave.net:453',
  timeout,
});

export const intradayPaymentsFetcher = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_PAYMENTS_BASE_URL ||
    'http://localhost:3002/bw-intraday-payments/apis/intraday/',
  headers: {
    timestamp: new Date().getTime(),
  },
  timeout,
});

export const intradaySubscriptionHistoryFetcher = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_PAYMENTS_BASE_URL?.replace(/\/intraday\/?$/, '') ??
    '',
  headers: {
    timestamp: new Date().getTime(),
  },
  timeout,
});

export const intradaySubscriptionFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_USER_PORTFOLIO_URL,
  headers: {
    timestamp: new Date().getTime(),
  },
  timeout,
});

export const intradayPortfolioBusinessFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BUSINESS_BASE_URL,
  headers: {
    timestamp: new Date().getTime(),
  },
  timeout,
});

export const downloadFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_INTRADAY_BASE_URL,
  timeout,
  responseType: 'blob',
});

export const profileFetcher = axios.create({
  baseURL: USER_AUTH_BASE_URL,
  timeout,
});
export const intradaySubscriptionsEndpoints = {
  getSavedDetails: (resourceId?: string, product?: string) =>
    `/agreement/?resource_id=${resourceId}&product=${product}`,
  initiatePayment: '/payments/initiatePayment',
  updatePaymentStatus: '/payments/updatePaymentStatus',
  saveDetails: '/agreement/',
  kraVerify: '/kra/verify',
  getAgreement: '/agreement/html',
  uploadAgreement: '/upload/agreement',
  downloadAgreement: (userId: string, resourceId: string, product: string) =>
    `/agreement/download-url?user_id=${userId}&resource_id=${resourceId}&product=${product}`,
};

export const intradayPaymentsEndpoints = {
  getPlans: (broker?: string) => `/getUserPlans?broker=${broker}`,
  initiatePayment: '/payments/initiatePayment',
  updatePaymentStatus: '/payments/updatePaymentStatus',
  getFreeTrialStatus: '/getFreeTrialStatus',
  activeFreeTrial: '/freeTrial',
  getRiskQuestionnaire: (analystId?: string) =>
    `/risk-assessment/?analyst_id=${analystId}`,
  getSubscriptionHistory: (
    userId: string,
    broker: string,
    type: 'all' | 'paid' | 'free',
    expiredActive: boolean
  ) =>
    `/subscriptions?broker=${broker}&type=${type}&userId=${userId}&expiredActive=${expiredActive}`,
};
export const marketPricerFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_MARKET_PRICER_BASE_URL,
  timeout,
});

/* Attach request interceptors to fetchers to to send requestToken  */
// biome-ignore lint/complexity/noForEach: <explanation>
[
  authFetcher,
  intradayPrivateFetcher,
  intradayPaymentsFetcher,
  intradaySubscriptionFetcher,
  downloadFetcher,
  profileFetcher,
  marketPricerFetcher,
  intradaySubscriptionHistoryFetcher,
].forEach((fetcher) => {
  fetcher.interceptors.request.use(attachAuthorization, requestErrorHandler);
  fetcher.interceptors.response.use(
    (response) => response,
    requestErrorHandler
  );
});

export const AuthEndpoints = {
  myProfile: '/user-app/user/profile/',
  updateProfile: 'user-app/user/profile',
  login: '/user-app/user/login',
  ssoClient: (broker: string) => `/user-app/user/${broker}/sso`,
  disclaimer: (broker: string) =>
    `/user-app/user/${broker.toLowerCase()}/disclaimer/`,
};

export const CycleEndpoints = {
  cycles: '/orders/cycles',
  createCycles: '/orders/cycles/',
  repairCycle: '/orders/repair',
  getCycleDetails: (cycleId: number | string) => `/orders/cycles/${cycleId}`,
  createSquareOff: '/orders/cycles/square-off/',
};

export const ProfileEndpoints = {
  profile: '/user-app/user/profile/',
  updateProfile: '/user-app/user/details/',
};

export const RecommendationsEndpoints = {
  preRecommendationDetails: '/recommendations/pre_recommendation_details/',
  recommendations: (type: string) => `/recommendations/${type}`,
};

export const getPricesEndpoint = '/monitoring/prices';
export const getChargesEndpoint = '/orders/brokerages';
export const getUserBalanceEndpoint = (broker: string) =>
  `/brokers/${broker}/client/balance`;

export const DownloadEndPoint = {
  pastPortfolio: '/user/cycle-download',
};

export const MarketPricerEndpoints = {
  live: '/live',
};
